import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';

export interface LoadingSpinnerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Spinner size */
  size?: 'sm' | 'md' | 'lg';
  /** Optional label shown next to the spinner */
  label?: string;
  /** Center the spinner in its container */
  centered?: boolean;
}

const sizeMap = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-10 w-10',
};

/**
 * Loading spinner using the lucide `Loader2` icon.
 *
 * @example
 * ```tsx
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" label="Loading work orders..." centered />
 * ```
 */
export const LoadingSpinner = React.forwardRef<
  HTMLDivElement,
  LoadingSpinnerProps
>(({ size = 'md', label, centered = false, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'inline-flex items-center gap-2 text-muted-foreground',
      centered && 'w-full justify-center p-8',
      className
    )}
    role="status"
    aria-live="polite"
    {...props}
  >
    <Loader2 className={cn('animate-spin', sizeMap[size])} />
    {label && <span className="text-sm">{label}</span>}
  </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';
